import React, { useEffect } from "react";
import usePaymentSettingsForm from "../hooks/usePaymentSettingsForm";
import {
  SectionTitle,
  SectionTagline,
  SwitchInput,
  KlarnaSettings,
} from "../components";

import { useForm } from "react-hook-form";
import { useUser } from "@auth";
import { formatAmount } from "@utils";
import { ProfilePaymentIcon } from "@components";

const General = () => {
  const {
    merchant,
    paymentSettings,
    paymentRequests,
    checkout,
    corporateCardSurchargePercent,
    klarnaAccountEmail,
    passOnCorporateCardFee,
    checkoutPassOnCorporateCardFee,
    terminals,
    submitPaymentSettings,
  } = usePaymentSettingsForm();

  const posEnabled = !!terminals?.enabled;

  const { user } = useUser();

  const {
    getValues,
    setValue,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      paymentRequests,
      checkout,
      klarnaAccountEmail,
      klarnaEnabled: !!klarnaAccountEmail,
      passOnCorporateCardFee,
      checkoutPassOnCorporateCardFee,
    },
  });

  useEffect(() => {
    if (merchant) {
      reset({
        paymentRequests,
        checkout,
        klarnaAccountEmail,
        klarnaEnabled: !!klarnaAccountEmail,
        passOnCorporateCardFee,
        checkoutPassOnCorporateCardFee,
      });
    }
  }, [merchant, paymentSettings]);

  if (!merchant) return null;

  const onInputChange = (name, value) => {
    if (name === 'passOnCorporateCardFee') {
      // One toggle drives all three channels: payment links, e-commerce checkout,
      // and (when the merchant has terminals) POS terminals.
      setValue('passOnCorporateCardFee', value);
      setValue('checkoutPassOnCorporateCardFee', value);

      const values = getValues();
      if (posEnabled) {
        values.terminals = { ...terminals, passOnCorporateCardFee: value };
      }

      onSubmit(values);
    } else {
      setValue(name, value);
      onSubmit(getValues());
    }
  };

  const onKlarnaEnable = async (requestedKlarnaEmail) => {
    await onSubmit({ ...getValues(), klarnaAccountEmail: requestedKlarnaEmail });
  };

  const onSubmit = async (values) => {
    let corporateCardSurchargeToUpdate = corporateCardSurchargePercent;

    if (values.passOnCorporateCardFee) {
      const currentPercent = corporateCardSurchargePercent ?? 0;
      corporateCardSurchargeToUpdate = currentPercent > 0 ? currentPercent : 2.0;
    }

    await submitPaymentSettings({
      ...values,
      corporateCardSurchargePercent: corporateCardSurchargeToUpdate,
    });
  };

  const formatAmountNoTrailingZeros = (value) => {
    const formatted = formatAmount(value);
    if (!formatted) return formatted;

    return formatted.replace(/(\.\d{2})$/, (decimals) =>
      decimals === ".00" ? "" : decimals
    );
  };

  const CCSurcharge = corporateCardSurchargePercent || 2;

  return (
    <div className="flex flex-col items-center py-4 px-5 overflow-auto">
      <form className="py-8 w-full max-w-4xl flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-1">
          <SectionTitle>General</SectionTitle>
          <SectionTagline>
            Configure General payment settings.
            {user.memberships.length > 1 && (
              <>{" "}These changes apply to your account with <b>{merchant.name}</b></>
          )}
          </SectionTagline>
        </div>

        <div className="rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-8 px-6 w-full">
          <div className="flex items-start gap-4">
            <ProfilePaymentIcon paymentType="Card" />
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Pass on corporate card fees</p>
                  <p className="text-sm/6 text-zinc-500 mt-0.5">
                    Apply a <strong>{CCSurcharge}%</strong> fee to corporate card payments (e.g. £1000 →{" "}
                    {formatAmountNoTrailingZeros(1000 + 1000 * (CCSurcharge / 100))}).
                    <br/>
                    Apple Pay / Google Pay will be disabled.
                    {posEnabled && (
                      <>
                        <br/>
                        Also applies to card payments taken on your POS terminals — terminals
                        pick up the change when they next check in.
                      </>
                    )}
                  </p>
                </div>
                <SwitchInput
                  name="passOnCorporateCardFee"
                  control={control}
                  onChange={(val) => {
                    onInputChange("passOnCorporateCardFee", val)
                  }
                }
                />
              </div>
            </div>
          </div>
        </div>

        <KlarnaSettings
          merchant={merchant}
          control={control}
          errors={errors}
          watch={watch}
          klarnaAccountEmail={klarnaAccountEmail}
          onInputChange={onInputChange}
          onEnableSubmit={onKlarnaEnable}
        />
      </form>
    </div>
  );
};

export default General;
